import { useToast } from '@/composables/useToast'
import { trackComponentError } from '@/utils/error-tracker'
import type { AxiosResponse } from 'axios'
import { ApiErrorHandler } from './error-handler'

export interface ApiResponse<T = any> {
  code: number
  data: T
  message: string
}

export interface ResponseHandlerOptions {
  showSuccessToast?: boolean
  showErrorToast?: boolean
  successMessage?: string
  errorMessage?: string
  redirectOnAuthError?: boolean
  customErrorHandler?: (error: unknown) => boolean
  component?: string // for logging
  action?: string // for logging
}

/**
 * Centralized server response handler
 */
export class ResponseHandler {
  private showSuccess
  private showError

  constructor() {
    const { showSuccess, showError } = useToast()
    this.showSuccess = showSuccess
    this.showError = showError
  }

  /**
   * Handles successful server response
   */
  handleSuccess<T>(
    response: AxiosResponse<ApiResponse<T>>,
    options: ResponseHandlerOptions = {}
  ): T {
    const { data } = response

    if (data.code !== 0) {
      // This is an API error, but we need to preserve the original data structure
      // for proper error code handling in ApiErrorHandler
      const apiError = {
        code: data.code,
        message: data.message,
        originalResponse: response
      }
      throw apiError
    }

    // Show success toast if needed
    if (options.showSuccessToast) {
      const message = options.successMessage || data.message || 'Operation completed successfully'
      this.showSuccess(message)
    }

    return data.data
  }

  /**
   * Handles server error
   */
  handleError(error: unknown, options: ResponseHandlerOptions = {}): never {
    // Check custom error handler first
    if (options.customErrorHandler && options.customErrorHandler(error)) {
      // Error was handled by custom handler
      throw error
    }

    // Use centralized error handler
    const errorHandler = new ApiErrorHandler(error)
    const errorCode = errorHandler.getErrorCode()
    const errorMessage = errorHandler.getErrorMessage()

    if (options.component && options.action) {
      trackComponentError(
        error instanceof Error ? error : new Error(errorMessage),
        options.component,
        options.action,
        false, // don't show toast here, show below
        { additional: { errorCode, endpoint: options.action } }
      )
    }

    // Show error toast if needed (skip for 2FA required error)
    if (options.showErrorToast !== false && errorCode !== 7004) {
      const message = options.errorMessage || errorMessage
      this.showError(message)
    }

    // Handle specific errors
    if (options.redirectOnAuthError !== false) {
      errorHandler.handle(true)
    }

    // Rethrow error
    throw error
  }

  /**
   * Universal method for handling promises with automatic toast display
   */
  async handlePromise<T>(
    promise: Promise<AxiosResponse<ApiResponse<T>>>,
    options: ResponseHandlerOptions = {}
  ): Promise<T> {
    try {
      const response = await promise
      return this.handleSuccess(response, options)
    } catch (error) {
      return this.handleError(error, options)
    }
  }
}

/**
 * Hook for using response handler
 */
export function useResponseHandler() {
  const handler = new ResponseHandler()

  return {
    handleSuccess: handler.handleSuccess.bind(handler),
    handleError: handler.handleError.bind(handler),
    handlePromise: handler.handlePromise.bind(handler)
  }
}

/**
 * Utilities for quick creation of handler options
 */
export const ResponseOptions = {
  /**
   * Show only success toast
   */
  successOnly: (message?: string): ResponseHandlerOptions => ({
    showSuccessToast: true,
    showErrorToast: false,
    successMessage: message
  }),

  /**
   * Show only error toast
   */
  errorOnly: (message?: string): ResponseHandlerOptions => ({
    showSuccessToast: false,
    showErrorToast: true,
    errorMessage: message
  }),

  /**
   * Show both toasts
   */
  both: (successMessage?: string, errorMessage?: string): ResponseHandlerOptions => ({
    showSuccessToast: true,
    showErrorToast: true,
    successMessage,
    errorMessage
  }),

  /**
   * Don't show toasts
   */
  silent: (): ResponseHandlerOptions => ({
    showSuccessToast: false,
    showErrorToast: false
  }),

  /**
   * Custom error handling
   */
  custom: (
    customErrorHandler: (error: unknown) => boolean,
    options: Partial<ResponseHandlerOptions> = {}
  ): ResponseHandlerOptions => ({
    showSuccessToast: false,
    showErrorToast: false,
    customErrorHandler,
    ...options
  }),

  withTracking: (
    component: string,
    action: string,
    options: Partial<ResponseHandlerOptions> = {}
  ): ResponseHandlerOptions => ({
    showErrorToast: true,
    component,
    action,
    ...options
  })
}
